import React, { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Paperclip } from 'lucide-react';
import BotaoVoltar from '../../../components/BotaoVoltar/BotaoVoltar';
import './style.css';
import {
  FundoForm, Classe, Conta, VinculoPrestador, DocumentoAnexo,
  classeVazia, contaVazia, prestadorVazio,
} from './types';
import {
  Opt, FORMA_JURIDICA, MASTER_FIC, MOEDA, JUROS, CLASSE_ANGA_1, CLASSE_ANGA_2,
  PAPEL, DOCS_OBRIGATORIOS, anbimaN1Opts, anbimaN2Opts, anbimaN3Opts,
} from './mock';
import {
  TextField, DateField, SelectField, CheckboxField, CnpjField, cnpjValido,
} from './fields';
import { ClasseSubform, ContaSubform, PrestadorSubform, DocumentoSubform } from './subforms';
// [DEV] botão de preenchimento de teste — desativado para deploy
// import { FUNDOS_EXEMPLO } from './devSeed';
import Revisao from './revisao';

// [DEV] gate do botão de preenchimento (descomente junto com o bloco abaixo p/ testar localmente)
// const DEV = process.env.NODE_ENV !== 'production';
import { useToast } from '../../../components/toast_notification/ToastProvider';
import { cadastrarFundo, listarPrestadorOpts, consultarCnpjPublico } from './fundosService';

let _seq = 1;
const novaKey = () => `k${_seq++}`;

const DRAFT_KEY = 'cadastro_fundo_rascunho';

// Mensagem de erro legível: detalha erros de validação (pydantic) campo a campo.
const formatarErro = (e: any): string => {
  const base = e?.message ?? 'erro desconhecido';
  const det = e?.details;
  if (Array.isArray(det) && det.length) {
    const linhas = det
      .map((d: any) => {
        const campo = Array.isArray(d?.loc) ? d.loc.filter((x: any) => x !== 'body').join('.') : '';
        return campo ? `${campo}: ${d?.msg ?? ''}` : d?.msg;
      })
      .filter(Boolean);
    if (linhas.length) return `${base} — ${linhas.join('; ')}`;
  }
  return base;
};

const STEPS = ['Identificação', 'Dados gerais', 'Classificação', 'Classes', 'Contas', 'Prestadores', 'Documentos', 'Revisão'];

const estadoInicial = (): FundoForm => ({
  cnpj: '', razao_social: '', nome_fantasia: '',
  moeda: 'BRL',
  is_active: true, count_on_aum: true, gera_lamina: false, paga_gestao: true,
  classes: [classeVazia(novaKey())], // fundo simples já começa com 1 classe "Única"
  contas: [],
  prestadores: [],
  documentos: [],
});

// Serializa o form ignorando os _key voláteis — usado p/ comparar com o estado
// inicial vazio e decidir se há rascunho de verdade (em vez de "pular o 1º render",
// que não é à prova de StrictMode e acabava salvando rascunho vazio).
const semKeys = (f: FundoForm): string =>
  JSON.stringify(f, (k, val) => (k === '_key' ? undefined : val));
const BASELINE_VAZIO = semKeys(estadoInicial());

type SubformAberto =
  | { tipo: 'classe'; item: Classe }
  | { tipo: 'conta'; item: Conta }
  | { tipo: 'prestador'; item: VinculoPrestador }
  | { tipo: 'documento'; item: DocumentoAnexo }
  | null;

const CadastroFundoLiquido: React.FC<{ onVoltar?: () => void; onCadastrado?: (fundoId: number) => void }> = ({ onVoltar, onCadastrado }) => {
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0); // etapa mais avançada já alcançada
  const [form, setForm] = useState<FundoForm>(estadoInicial);
  const [subform, setSubform] = useState<SubformAberto>(null);
  const [tentouAvancar, setTentouAvancar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [prestadorOpts, setPrestadorOpts] = useState<Opt[]>([]);
  const [temRascunho, setTemRascunho] = useState(false);
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const cnpjBuscado = useRef('');        // último CNPJ já consultado (evita refetch)
  // [DEV] const [seedOpen, setSeedOpen] = useState(false); // dropdown do botão "Preencher"

  const set = (campo: keyof FundoForm, v: any) => setForm((p) => ({ ...p, [campo]: v }));

  // Prestadores reais da API (substitui o mock; evita FK quebrado / "erro de gestora").
  useEffect(() => {
    listarPrestadorOpts()
      .then(setPrestadorOpts)
      .catch((e: any) => toast.error(`Não foi possível carregar prestadores: ${e?.message ?? 'erro'}`));
  }, [toast]);

  // Rascunho: restaura ao abrir, se houver algo salvo (e não-vazio) de uma sessão anterior.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as FundoForm;
      if (semKeys(parsed) === BASELINE_VAZIO) {
        localStorage.removeItem(DRAFT_KEY); // rascunho vazio (legado/StrictMode): descarta
        return;
      }
      setForm(parsed);
      setTemRascunho(true);
      toast.info('Rascunho recuperado. Continue de onde parou.');
    } catch { /* rascunho corrompido: ignora */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rascunho: salva a cada alteração — mas só se o form tiver conteúdo de verdade.
  // Se estiver igual ao estado inicial vazio, limpa o rascunho (não marca temRascunho).
  useEffect(() => {
    try {
      if (semKeys(form) === BASELINE_VAZIO) {
        localStorage.removeItem(DRAFT_KEY);
        setTemRascunho(false);
        return;
      }
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      setTemRascunho(true);
    } catch { /* storage cheio/indisponível: ignora */ }
  }, [form]);

  // Autopreenche razão social / nome fantasia consultando o CNPJ (BrasilAPI)
  // assim que os 14 dígitos são digitados. Falha silenciosa — não atrapalha o cadastro.
  useEffect(() => {
    const d = (form.cnpj || '').replace(/\D/g, '');
    if (d.length !== 14 || d === cnpjBuscado.current) return;
    cnpjBuscado.current = d;
    setBuscandoCnpj(true);
    consultarCnpjPublico(d)
      .then((info) => {
        if (info && (info.razao_social || info.nome_fantasia)) {
          setForm((p) => ({
            ...p,
            razao_social: info.razao_social || p.razao_social,
            nome_fantasia: info.nome_fantasia || p.nome_fantasia,
          }));
        }
      })
      .finally(() => setBuscandoCnpj(false));
  }, [form.cnpj]);

  const limparRascunho = () => {
    localStorage.removeItem(DRAFT_KEY);
    setTemRascunho(false);
    setForm(estadoInicial());
    setStep(0);
    setMaxStep(0);
    toast.info('Rascunho descartado.');
  };

  const cnpjDigitos = (form.cnpj || '').replace(/\D/g, '');
  const erros = {
    cnpj: !cnpjValido(form.cnpj),
    cnpjMsg: !form.cnpj
      ? 'Informe o CNPJ'
      : cnpjDigitos.length !== 14
        ? 'CNPJ deve ter 14 dígitos'
        : 'CNPJ inválido (dígito verificador incorreto)',
    razao_social: !form.razao_social.trim(),
  };
  const step0Invalido = erros.cnpj || erros.razao_social;

  const podeAvancar = () => {
    if (step === 0 && step0Invalido) { setTentouAvancar(true); return false; }
    return true;
  };
  const avancar = () => {
    if (!podeAvancar()) return;
    setTentouAvancar(false);
    const next = Math.min(step + 1, STEPS.length - 1);
    setStep(next);
    setMaxStep((m) => Math.max(m, next));
  };
  const voltar = () => setStep((s) => Math.max(s - 1, 0));
  const irParaStep = (i: number) => { if (i <= maxStep) setStep(i); }; // só etapas já visitadas

  // [DEV] preenche o form com um exemplo e libera todos os passos — desativado para deploy
  // const preencherExemplo = (build: () => FundoForm) => {
  //   const f = build();
  //   setForm(f);
  //   cnpjBuscado.current = (f.cnpj || '').replace(/\D/g, ''); // evita o auto-lookup sobrescrever a razão social
  //   setStep(0);
  //   setMaxStep(STEPS.length - 1);
  //   setTentouAvancar(false);
  //   setSeedOpen(false);
  //   toast.info('Formulário preenchido com dados de exemplo.');
  // };

  // ----- coleções (upsert por _key) -----
  const upsert = <T extends { _key: string }>(lista: T[], item: T): T[] => {
    const i = lista.findIndex((x) => x._key === item._key);
    if (i === -1) return [...lista, item];
    const novo = [...lista]; novo[i] = item; return novo;
  };
  const salvarSubform = (item: any) => {
    if (!subform) return;
    if (subform.tipo === 'classe') set('classes', upsert(form.classes, item));
    if (subform.tipo === 'conta') set('contas', upsert(form.contas, item));
    if (subform.tipo === 'prestador') set('prestadores', upsert(form.prestadores, item));
    if (subform.tipo === 'documento') {
      const i = form.documentos.findIndex((d) => d.tipo === item.tipo);
      set('documentos', i === -1 ? [...form.documentos, item] : form.documentos.map((d, idx) => (idx === i ? item : d)));
    }
    setSubform(null);
  };
  const remover = (tipo: 'classes' | 'contas' | 'prestadores', key: string) =>
    set(tipo, (form[tipo] as any[]).filter((x) => x._key !== key));
  const removerDocumento = (tipo: string) =>
    set('documentos', form.documentos.filter((d) => d.tipo !== tipo));

  const confirmar = async () => {
    if (enviando) return;
    setEnviando(true);
    try {
      const { fundoId, erroDocs } = await cadastrarFundo(form);
      const nome = form.nome_fantasia || form.razao_social || 'novo';
      // cadastro concluído: descarta o preenchimento (rascunho + form em memória)
      localStorage.removeItem(DRAFT_KEY);
      setTemRascunho(false);
      setForm(estadoInicial());
      setStep(0);
      setMaxStep(0);
      if (erroDocs) {
        toast.warning(`Fundo "${nome}" cadastrado (#${fundoId}), mas houve erro nos documentos: ${erroDocs}`);
      } else {
        toast.success(`Fundo "${nome}" cadastrado com sucesso (#${fundoId}).`);
      }
      // vai para a tela do fundo recém-criado
      if (fundoId) onCadastrado?.(Number(fundoId));
    } catch (e: any) {
      toast.error(`Falha ao cadastrar: ${formatarErro(e)}`);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fl-wizard">
      <div className="fl-head">
        <div className="fl-head-left">
          {onVoltar && <BotaoVoltar onClick={onVoltar} />}
          <h1 className="fl-title">Cadastro de Fundo</h1>
        </div>
        {/* [DEV] botão de preenchimento de teste — desativado para deploy
        {DEV && (
          <div className="fl-seed">
            <button type="button" className="fl-seed-btn" onClick={() => setSeedOpen((o) => !o)}>
              🧪 Preencher exemplo ▾
            </button>
            {seedOpen && (
              <ul className="fl-seed-menu">
                {FUNDOS_EXEMPLO.map((ex, i) => (
                  <li key={i}>
                    <button type="button" onClick={() => preencherExemplo(ex.build)}>{ex.label}</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        */}
      </div>

      <div className="fl-body">
      {/* Stepper lateral */}
      <div className="fl-stepper">
        {STEPS.map((label, i) => (
          <div
            key={label}
            className={`fl-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''} ${i > maxStep ? 'locked' : ''}`}
            onClick={() => irParaStep(i)}
            title={i > maxStep ? 'Conclua as etapas anteriores' : undefined}
          >
            <div className="fl-step-circle">{i < step ? '✓' : i + 1}</div>
            <div className="fl-step-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="fl-main">
      <div className="fl-scroll">
      <div className="fl-card">
        {step === 0 && (
          <>
            <div className="fl-grid">
              <CnpjField label="CNPJ" required value={form.cnpj} onChange={(v) => set('cnpj', v)}
                error={tentouAvancar && erros.cnpj ? erros.cnpjMsg : undefined} />
              <TextField label="Razão social" required wide value={form.razao_social} onChange={(v) => set('razao_social', v)}
                disabled={buscandoCnpj} placeholder={buscandoCnpj ? 'Buscando pelo CNPJ…' : undefined}
                error={tentouAvancar && erros.razao_social ? 'Obrigatório' : undefined} />
              <TextField label="Nome fantasia" wide value={form.nome_fantasia} onChange={(v) => set('nome_fantasia', v)}
                disabled={buscandoCnpj} placeholder={buscandoCnpj ? 'Buscando pelo CNPJ…' : undefined} />
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div className="fl-grid">
              <SelectField label="Moeda" value={form.moeda} onChange={(v) => set('moeda', v)} options={MOEDA} />
              <SelectField label="Master / FIC" value={form.master_fic} onChange={(v) => set('master_fic', v)} options={MASTER_FIC}
                hint="Master: fundo que recebe aplicações de outros fundos. FIC: Fundo de Investimento em Cotas, que aplica em outro(s) fundo(s)." />
              <TextField label="Código ANBIMA" value={form.codigo_anbima} onChange={(v) => set('codigo_anbima', v)}
                maxLength={20} hint="Código de identificação do fundo na ANBIMA (até 20 caracteres)." />
              <DateField label="Data de constituição/início" value={form.data_lancamento} onChange={(v) => set('data_lancamento', v)} />
              <DateField label="Data de encerramento" value={form.data_encerramento} onChange={(v) => set('data_encerramento', v)} />
              <TextField label="Voice" value={form.voice} onChange={(v) => set('voice', v)}
                hint="Voice broker (B3): corretora/mesa e contato responsável por executar ordens do fundo por voz/telefone." />
              <SelectField label="Juros" value={form.juros} onChange={(v) => set('juros', v)} options={JUROS}
                hint="Regime de juros usado nos cálculos do fundo: simples ou compostos." />
            </div>
            <div className="fl-section-title">Flags</div>
            <div className="fl-checks">
              <CheckboxField label="Ativo" checked={form.is_active} onChange={(v) => set('is_active', v)} />
              <CheckboxField label="Conta no AUM" checked={form.count_on_aum} onChange={(v) => set('count_on_aum', v)} />
              <CheckboxField label="Gera lâmina" checked={form.gera_lamina} onChange={(v) => set('gera_lamina', v)} />
              <CheckboxField label="Paga gestão" checked={form.paga_gestao} onChange={(v) => set('paga_gestao', v)} />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="fl-grid fl-grid--3">
              <SelectField label="Classificação CVM" value={form.forma_juridica} onChange={(v) => set('forma_juridica', v)} options={FORMA_JURIDICA}
                hint="Tipo do fundo segundo a CVM (ex.: FIDC, FI, FIF, FIP, FII)." />
              <SelectField label="ANBIMA nível 1" value={form.classe_anbima_n1} options={anbimaN1Opts()}
                onChange={(v) => setForm((p) => ({ ...p, classe_anbima_n1: v, classe_anbima_n2: '', classe_anbima_n3: '' }))}
                hint="Classificação ANBIMA — nível 1: categoria macro do fundo (ex.: Renda Fixa, Ações, Multimercado, Cambial)." />
              <SelectField label="ANBIMA nível 2" value={form.classe_anbima_n2} options={anbimaN2Opts(form.classe_anbima_n1)}
                onChange={(v) => setForm((p) => ({ ...p, classe_anbima_n2: v, classe_anbima_n3: '' }))}
                hint="Classificação ANBIMA — nível 2: subcategoria/estratégia (opções definidas pelo nível 1)." />
              <SelectField label="ANBIMA nível 3" value={form.classe_anbima_n3} options={anbimaN3Opts(form.classe_anbima_n1, form.classe_anbima_n2)}
                onChange={(v) => set('classe_anbima_n3', v)}
                hint="Classificação ANBIMA — nível 3: detalhamento da estratégia (opções definidas pelo nível 2)." />
              <SelectField label="ANGA 1" value={form.classe_anga_1} onChange={(v) => set('classe_anga_1', v)} options={CLASSE_ANGA_1}
                hint="Classificação interna ANGA — nível 1 (ex.: Líquido ou Estruturado)." />
              <SelectField label="ANGA 2" value={form.classe_anga_2} onChange={(v) => set('classe_anga_2', v)} options={CLASSE_ANGA_2}
                hint="Classificação interna ANGA — nível 2 (ex.: Multimercado, FIDC, Renda Fixa, FIP)." />
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="fl-list-head">
              <button className="fl-add-btn" onClick={() => setSubform({ tipo: 'classe', item: classeVazia(novaKey()) })}>
                <Plus size={16} /> Adicionar classe
              </button>
            </div>
            {form.classes.length === 0 ? (
              <div className="fl-empty">Nenhuma classe adicionada.</div>
            ) : (
              <table className="fl-table">
                <thead><tr><th>Subclasse</th><th>Nome</th><th>Benchmark</th><th>Tx. adm</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {form.classes.map((c) => (
                    <tr key={c._key}>
                      <td>{c.subclasse}</td>
                      <td>{c.nome || '—'}</td>
                      <td>{c.benchmark || '—'}</td>
                      <td>{c.condicoes.tx_adm || '—'}</td>
                      <td><span className={`fl-badge ${c.is_active ? 'fl-badge--on' : 'fl-badge--off'}`}>{c.is_active ? 'Ativa' : 'Inativa'}</span></td>
                      <td>
                        <div className="fl-row-actions">
                          <button className="fl-icon-btn" onClick={() => setSubform({ tipo: 'classe', item: c })}><Pencil size={15} /></button>
                          <button className="fl-icon-btn danger" onClick={() => remover('classes', c._key)}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {step === 4 && (
          <>
            <div className="fl-list-head">
              <button className="fl-add-btn" onClick={() => setSubform({ tipo: 'conta', item: contaVazia(novaKey()) })}>
                <Plus size={16} /> Adicionar conta
              </button>
            </div>
            {form.contas.length === 0 ? (
              <div className="fl-empty">Nenhuma conta adicionada.</div>
            ) : (
              <table className="fl-table">
                <thead><tr><th>Tipo</th><th>Código</th><th>Banco</th><th>Agência</th><th>Conta</th><th></th></tr></thead>
                <tbody>
                  {form.contas.map((c) => (
                    <tr key={c._key}>
                      <td>{c.tipo}</td><td>{c.codigo || '—'}</td><td>{c.banco || '—'}</td>
                      <td>{c.agencia || '—'}</td><td>{c.numero_conta || '—'}</td>
                      <td>
                        <div className="fl-row-actions">
                          <button className="fl-icon-btn" onClick={() => setSubform({ tipo: 'conta', item: c })}><Pencil size={15} /></button>
                          <button className="fl-icon-btn danger" onClick={() => remover('contas', c._key)}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {step === 5 && (
          <>
            <div className="fl-list-head">
              <button className="fl-add-btn" onClick={() => setSubform({ tipo: 'prestador', item: prestadorVazio(novaKey()) })}>
                <Plus size={16} /> Adicionar prestador
              </button>
            </div>
            {form.prestadores.length === 0 ? (
              <div className="fl-empty">Nenhum prestador vinculado.</div>
            ) : (
              <table className="fl-table">
                <thead><tr><th>Prestador</th><th>Papel</th><th>Início</th><th>Fim</th><th></th></tr></thead>
                <tbody>
                  {form.prestadores.map((p) => (
                    <tr key={p._key}>
                      <td>{p.prestador_nome || '—'}</td>
                      <td>{PAPEL.find((o) => o.value === p.papel)?.label || '—'}</td>
                      <td>{p.data_inicio || '—'}</td><td>{p.data_fim || '—'}</td>
                      <td>
                        <div className="fl-row-actions">
                          <button className="fl-icon-btn" onClick={() => setSubform({ tipo: 'prestador', item: p })}><Pencil size={15} /></button>
                          <button className="fl-icon-btn danger" onClick={() => remover('prestadores', p._key)}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {step === 6 && (
          <>
            <table className="fl-table">
              <thead><tr><th>Documento</th><th>Arquivo</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {DOCS_OBRIGATORIOS.map((doc) => {
                  const anexo = form.documentos.find((d) => d.tipo === doc.tipo);
                  return (
                    <tr key={doc.tipo}>
                      <td>{doc.label}</td>
                      <td>{anexo?.nome_arquivo || '—'}</td>
                      <td>
                        <span className={`fl-badge ${anexo ? 'fl-badge--on' : 'fl-badge--off'}`}>
                          {anexo ? 'Anexado' : 'Pendente'}
                        </span>
                      </td>
                      <td>
                        <div className="fl-row-actions">
                          <button className="fl-icon-btn" title={anexo ? 'Trocar arquivo' : 'Anexar documento'}
                            onClick={() => setSubform({ tipo: 'documento', item: anexo ?? { tipo: doc.tipo } })}>
                            <Paperclip size={16} />
                          </button>
                          {anexo && (
                            <button className="fl-icon-btn danger" title="Remover arquivo"
                              onClick={() => removerDocumento(doc.tipo)}>
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        {step === 7 && <Revisao form={form} />}
      </div>
      </div>

      {/* Navegação */}
      <div className="fl-footer">
        {temRascunho && (
          <button className="fl-btn fl-btn--text" onClick={limparRascunho} title="Apaga o rascunho salvo e recomeça">
            Descartar rascunho
          </button>
        )}
        <button className="fl-btn fl-btn--text" onClick={voltar} disabled={step === 0}>Voltar</button>
        {step < STEPS.length - 1 ? (
          <button className="fl-btn fl-btn--primary" onClick={avancar}>Próximo</button>
        ) : (
            <button className="fl-btn fl-btn--primary" onClick={confirmar} disabled={enviando}>
              {enviando ? 'Enviando...' : 'Confirmar cadastro'}
            </button>
        )}
      </div>
      </div>
      </div>

      {/* Subforms */}
      {subform?.tipo === 'classe' && (
        <ClasseSubform initial={subform.item} onSave={salvarSubform} onClose={() => setSubform(null)} />
      )}
      {subform?.tipo === 'conta' && (
        <ContaSubform initial={subform.item} onSave={salvarSubform} onClose={() => setSubform(null)} />
      )}
      {subform?.tipo === 'prestador' && (
        <PrestadorSubform initial={subform.item} options={prestadorOpts} onSave={salvarSubform} onClose={() => setSubform(null)} />
      )}
      {subform?.tipo === 'documento' && (
        <DocumentoSubform
          initial={subform.item}
          label={DOCS_OBRIGATORIOS.find((x) => x.tipo === subform.item.tipo)?.label ?? subform.item.tipo}
          onSave={salvarSubform}
          onClose={() => setSubform(null)}
        />
      )}
    </div>
  );
};

export default CadastroFundoLiquido;
